import { Component, inject, Input, OnChanges } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { PermissionService } from '../../../../core/services/permission.service';
import { KEYCLOAK_ROLES } from '../../../../shared/constants/auth.constants';
import { DashboardCatalog, DashboardChannel, DashboardIntegrationHealth } from '../../models/dashboard.model';
import {
  CatalogLine,
  catalogLine,
  ConnectionLine,
  connectionFacts,
  connectionLine,
} from '../../utils/dashboard-connections';

// Conexões (spec §10): uma linha por integração com ponto de status, nome e o estado em texto
// ("Ativa, sem fila", "N na fila", "Limite de chamadas até HH:mm", "Inativa" com a mensagem). O botão
// "Reautorizar" só aparece na integração inativa. Abaixo, anúncios e produtos do catálogo, para quem
// tem `advertisement`, e só se a empresa tem algum. Sem vencimento de token: ele renova sozinho.
@Component({
  selector: 'app-dashboard-connections',
  standalone: true,
  imports: [RouterModule, NgbTooltipModule],
  host: { class: 'col-12 col-lg-6' },
  styles: [
    `
      :host {
        min-width: 0;
      }

      .conn-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(--bs-secondary-color, #a1acb8);
        margin-top: 0.3rem;
        align-self: flex-start;
      }

      .conn-dot--ok {
        background: #28c76f;
        box-shadow: 0 0 0 3px rgba(40, 199, 111, 0.18);
      }

      .conn-dot--degraded {
        background: #ff9f43;
        box-shadow: 0 0 0 3px rgba(255, 159, 67, 0.2);
      }

      .conn-dot--down {
        background: #ea5455;
        box-shadow: 0 0 0 3px rgba(234, 84, 85, 0.2);
      }

      /* Mensagem da integração pode ser longa: quebra em vez de alargar o card. */
      .conn-text {
        overflow-wrap: anywhere;
      }

      .conn-catalog {
        display: flex;
        flex-wrap: wrap;
        gap: 0.25rem 1rem;
        margin-top: 0.5rem;
        padding-top: 0.75rem;
        border-top: 1px solid var(--bs-border-color-translucent, rgba(67, 89, 113, 0.08));
        font-size: 0.8125rem;
      }

      .conn-catalog a {
        text-decoration: none;
        color: inherit;
      }

      .conn-catalog a:hover {
        text-decoration: underline;
      }

      .conn-catalog b {
        font-variant-numeric: tabular-nums;
      }
    `,
  ],
  template: `
    <div class="card h-100">
      <div class="card-header d-flex align-items-center justify-content-between">
        <h5 class="card-title mb-0 me-2">Conexões</h5>
        <a class="btn btn-sm btn-icon btn-text-secondary rounded-circle flex-shrink-0" routerLink="/integracoes"
          ngbTooltip="Ver integrações" container="body" aria-label="Ver integrações">
          <i class="ti ti-arrow-up-right"></i>
        </a>
      </div>
      <div class="card-body">
        @for (l of lines; track l.id) {
          <div class="dash-list-item" [attr.data-connection]="l.id">
            <span [class]="'conn-dot flex-shrink-0 conn-dot--' + l.state" aria-hidden="true"></span>
            <span class="dash-list-main">
              <span class="dash-list-name conn-text">{{ l.name }}</span>
              <span class="stat-caption conn-text" data-state
                [class.text-warning]="l.state === 'degraded'" [class.text-danger]="l.state === 'down'">{{ l.text }}</span>
              @if (l.message) {
                <span class="stat-caption conn-text text-danger" data-message>{{ l.message }}</span>
              }
            </span>
            @if (l.reauthorize) {
              <a [routerLink]="['/integracoes/editar', l.id]" class="btn btn-xs btn-label-warning flex-shrink-0"
                data-reauthorize>Reautorizar</a>
            }
          </div>
        } @empty {
          <div class="text-center text-muted py-4">
            <i class="ti ti-plug-off d-block mb-2" style="font-size: 1.75rem; opacity: 0.5"></i>
            <p class="mb-0 small">Nenhuma integração cadastrada.</p>
          </div>
        }

        @if (catalog; as c) {
          <div class="conn-catalog" data-catalog>
            <a routerLink="/anuncios"><b>{{ c.published }}</b> {{ c.publishedLabel }}</a>
            @if (c.failed > 0) {
              <a routerLink="/anuncios" [queryParams]="{ channelStatus: 3 }" class="text-danger" data-ads-failed>
                <b>{{ c.failed }}</b> com falha
              </a>
            } @else {
              <span class="text-muted"><b>0</b> com falha</span>
            }
            <span><b>{{ c.products }}</b> {{ c.productsLabel }}</span>
          </div>
        }
      </div>
    </div>
  `,
})
export class DashboardConnectionsComponent implements OnChanges {
  /** `channels` do snapshot novo. */
  @Input() channels?: DashboardChannel[] | null;
  /** Integrações do snapshot antigo: usadas só quando `channels` não vem. */
  @Input() integrations?: DashboardIntegrationHealth[] | null;
  @Input() catalogData?: DashboardCatalog | null;

  private readonly permissions = inject(PermissionService);

  // Montados uma vez por mudança de entrada: referência estável pros links do router.
  lines: ConnectionLine[] = [];
  private catalogCache: CatalogLine | null = null;

  ngOnChanges(): void {
    const now = new Date();
    this.lines = connectionFacts(this.channels, this.integrations).map((c) => connectionLine(c, now));
    this.catalogCache = catalogLine(this.catalogData);
  }

  /** A linha de anúncios e produtos é de quem tem `advertisement`. */
  get catalog(): CatalogLine | null {
    return this.permissions.has(KEYCLOAK_ROLES.ADVERTISEMENT) ? this.catalogCache : null;
  }
}
